import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Container,
    InputAdornment,
    LinearProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import { observer } from "mobx-react";
import React from "react";
import { Grid } from "@mui/material";
import Fetch from "../api/Fetch";
import { BACKEND_URI, T1_BACKEND_URI } from "../constants";
import { Order, OrderStatus, OrderStatusFriendlyName } from "../model/Order";
import AuthStore from "../stores/AuthStore";
import Toaster from "../common/Toaster";
import SearchIcon from "@mui/icons-material/Search";
import ReceiptIcon from "@mui/icons-material/Receipt";
import EmailIcon from "@mui/icons-material/Email";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const OrderLookup = observer(() => {
    const [search, setSearch] = React.useState("");
    const [orders, setOrders] = React.useState<Order[]>([]);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        getOrderList();
    }, []);

    const getOrderList = async () => {
        setLoading(true);
        try {
            const data = await Fetch<Order[]>(`${T1_BACKEND_URI}/lookup?searchText=${search}`, {
                includeAuth: true,
            });
            setOrders(data);
        } catch (err: any) {
            console.log(err);
            Toaster.error(err.message || "An error occurred");
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 500);
        }
    };

    const onSubmit = (e: any) => {
        e.preventDefault();
        getOrderList();
    };

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Typography variant="h4" sx={{ mb: 3, fontWeight: 500 }}>
                Order Lookup
            </Typography>
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Box component="form" onSubmit={onSubmit}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={8}>
                            <TextField
                                fullWidth
                                label="Search by order ID, name, or email"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                variant="outlined"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Button
                                variant="contained"
                                type="submit"
                                fullWidth
                                size="large"
                                startIcon={<SearchIcon />}
                                sx={{ height: 56, borderRadius: 2 }}
                                disabled={loading}
                            >
                                Search Orders
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>
            <Paper elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "primary.light" }}>
                                <TableCell sx={{ color: "text.primary", fontWeight: 600 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <ReceiptIcon fontSize="small" />
                                        Order ID
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ color: "text.primary", fontWeight: 600 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <PersonIcon fontSize="small" />
                                        Customer
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ color: "text.primary", fontWeight: 600 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <EmailIcon fontSize="small" />
                                        Email
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ color: "text.primary", fontWeight: 600 }}>Status</TableCell>
                                <TableCell sx={{ color: "text.primary", fontWeight: 600 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <CalendarTodayIcon fontSize="small" />
                                        Date
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ color: "text.primary", fontWeight: 600 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <AttachMoneyIcon fontSize="small" />
                                        Total
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ color: "text.primary", fontWeight: 600 }}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} sx={{ textAlign: "center", py: 4 }}>
                                        <LinearProgress />
                                    </TableCell>
                                </TableRow>
                            ) : orders.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} sx={{ textAlign: "center", py: 6 }}>
                                        <Typography variant="h6" color="text.secondary">
                                            No orders found
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                            Try searching with a different term
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                orders.map((it, index) => (
                                    <TableRow
                                        key={it.id}
                                        sx={{
                                            "&:hover": { backgroundColor: "action.hover" },
                                            backgroundColor: index % 2 === 0 ? "background.paper" : "action.disabledBackground",
                                        }}
                                    >
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="medium">
                                                #{it.id}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{it.name}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                                                {it.email}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={OrderStatusFriendlyName(it.orderStatus)}
                                                size="small"
                                                color={
                                                    it.orderStatus === OrderStatus.PURCHASED
                                                        ? "success"
                                                        : it.orderStatus === OrderStatus.REFUNDED
                                                        ? "error"
                                                        : "warning"
                                                }
                                                variant="filled"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{DateTime.fromMillis(it.dateCreated).toFormat("MMM dd, yyyy")}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {DateTime.fromMillis(it.dateCreated).toFormat("t")}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body1" fontWeight="medium">
                                                ${(it.total / 100).toFixed(2)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {it.orderStatus === OrderStatus.PURCHASED && (
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    startIcon={<OpenInNewIcon />}
                                                    onClick={() => {
                                                        window.open(
                                                            `https://shop.authentigate.ca/store/${it.cartUrl}/thank-you/${it.retrievalKey}`,
                                                            "_blank"
                                                        );
                                                    }}
                                                    sx={{ borderRadius: 2 }}
                                                >
                                                    View
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Container>
    );
});

export default OrderLookup;
